// The buyer's side of sending goods back, in an order's "Request for return"
// tab: a new request (a dialog like the cart — pick the items, say why), the
// requests made, cancelled while the seller has not answered, and the seller's
// answer once it came (accepted: what happens next; declined: why). The
// server keeps the rules (aldoo-na/store/returns): what can still be asked
// back, the reasons, the return period.
import React, { useMemo, useState } from "react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showErrorToast, showSuccessToast } from "aldoo-ra/Toast"
import { describeLine } from "aldoo-ra/Store/lines"
import useT from "../hooks/useT"
import useSiteLanguage from "../hooks/useSiteLanguage"
import Img from "../ui/Img"
import { ReturnRequest } from "../storeApi"
import { formatMoney } from "./ui"
import { BuyerAvatar, SellerAvatar } from "aldoo-ra/StoreOrders/Avatars"

/** What each reason is called (aldoo-na/store/returns BUYER_REASONS), in English: said through t(). */
const REASON_LABELS = {
  withdrawal: "Changed my mind",
  wrong_size: "Wrong size or fit",
  not_as_described: "Not as described",
  wrong_item: "Wrong item sent",
  defective: "Defective",
  damaged_in_transit: "Damaged in transit",
  other: "Other",
}

const STATUS = {
  open: { label: "Waiting for the seller", cls: "bg-[#ff9800] text-black" },
  // accepted: the seller now waits for the items to come back
  accepted: { label: "Waiting for items", cls: "bg-[#4caf50] text-white" },
  returned: { label: "Item returned", cls: "bg-[#6b5ca5] text-white" },
  declined: { label: "Declined", cls: "bg-[#f44336] text-white" },
  canceled: { label: "Canceled", cls: "bg-neutral text-text_secondary" },
}

const formatDate = (value, language) => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  try {
    return new Intl.DateTimeFormat(language || "en", { day: "numeric", month: language === "en" ? "short" : "long", year: "numeric" }).format(d)
  } catch {
    return d.toDateString()
  }
}

const Thumb = ({ line, size = 44 }) => <Img src={line.square || line.thumb} alt={line.name} width={size} height={size} className="rounded-lg overflow-hidden shrink-0" style={{ width: size, height: size }} />

/** A stepper of whole units, none to `max`. */
function Stepper({ value, max, onChange }) {
  const btn = "w-8 h-8 rounded-full border border-bordered flex items-center justify-center text-[18px] leading-none disabled:opacity-40"
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid="return-request-quantity">
      <button type="button" className={btn} disabled={value <= 1} onClick={() => onChange(Math.max(1, value - 1))} aria-label="−">
        −
      </button>
      <span className="w-6 text-center tabular-nums text-[15px] font-semibold">{value}</span>
      <button type="button" className={btn} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} aria-label="+">
        +
      </button>
    </div>
  )
}

/** The new request: the items, like the cart; then why; then send. Full screen on a phone. */
function NewRequestDialog({ order, returns, onClose, onSent }) {
  const t = useT()
  const language = useSiteLanguage()
  const lines = useMemo(() => (returns.lines || []).filter((line) => line.requestable > 0), [returns])
  // the quantity of each picked line, by its cart index; nothing is picked at first
  const [picked, setPicked] = useState({})
  const [reason, setReason] = useState("")
  const [note, setNote] = useState("")
  const [busy, setBusy] = useState(false)
  const reasons = (returns.reasons || Object.keys(REASON_LABELS).map((id) => ({ id, note: id === "other" }))).map((entry) => ({ ...entry, value: entry.id, name: t(REASON_LABELS[entry.id] || entry.id) }))
  const chosen = reasons.find((entry) => entry.id === reason) || null
  const chosenLines = lines.filter((line) => picked[line.cartIndex] > 0)
  const noteMissing = !!chosen?.note && !note.trim()
  const ready = chosenLines.length > 0 && !!chosen && !noteMissing

  const send = async () => {
    setBusy(true)
    try {
      const res = await ReturnRequest({
        op: "create",
        order_id: order._id,
        reason,
        note: note.trim() || undefined,
        lines: chosenLines.map((line) => ({ pid: line.pid, ...(line.lineKey ? { lineKey: line.lineKey } : {}), quantity: picked[line.cartIndex] })),
      }).catch((e) => ({ error: e.message }))
      if (!res?.result) return showErrorToast(t(res?.message || res?.error || "The request could not be sent"))
      showSuccessToast(t`Your request is being processed`)
      onSent?.()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="sf_dialog"
      title={t`Request for return`}
      closeOnEscape
      footerContent={
        <Button variant="primary" className="w-full" onClick={send} disabled={!ready || busy} data-testid="return-request-send">
          {busy ? t`Sending…` : t`Send request`}
        </Button>
      }
      styleOverrides={{
        container: "w-full h-[100dvh] md:h-auto md:max-w-[640px] md:max-h-[calc(100vh-2rem)] bg-paper md:rounded-2xl shadow-2xl flex flex-col overflow-hidden",
        footer: "px-5 py-4 border-t border-bordered bg-paper shrink-0",
      }}
    >
      <div className="flex flex-col gap-5" data-testid="return-request-dialog">
        <div className="flex flex-col gap-1">
          <p className="m-0 text-[15px] font-semibold">{t`Select the items you want to return`}</p>
          {returns.window?.within === false && (
            <p className="m-0 text-[13px] text-text_secondary">{t("The return period ended on %s. You can still send a request: the seller will consider it, for example under the legal guarantee.", formatDate(returns.window.until, language))}</p>
          )}
        </div>
        <ul className="m-0 p-0 list-none flex flex-col divide-y divide-bordered">
          {lines.map((line) => {
            const on = picked[line.cartIndex] > 0
            return (
              <li key={line.cartIndex} className="flex items-center gap-3 py-3" data-testid="return-request-line">
                <Checkbox
                  variant="sf"
                  isOn={on}
                  onChange={(value) => setPicked((current) => ({ ...current, [line.cartIndex]: value ? line.requestable : 0 }))}
                />
                <Thumb line={line} />
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="text-[14px] font-semibold break-words">{describeLine(line, line.name || line.pid)}</span>
                  {line.price !== undefined && line.price !== null && <span className="text-[13px] text-text_secondary">{formatMoney(line.price, order.currency)}</span>}
                  {/* a product with its own period says until when it can come back */}
                  {line.returnUntil && returns.window?.until && new Date(line.returnUntil).toDateString() !== new Date(returns.window.until).toDateString() && (
                    <span className={`text-[12px] ${new Date(line.returnUntil) < new Date() ? "text-[#c62828]" : "text-text_secondary"}`}>{t("Return until %s", formatDate(line.returnUntil, language))}</span>
                  )}
                </div>
                {on && line.requestable > 1 ? (
                  <Stepper value={picked[line.cartIndex]} max={line.requestable} onChange={(value) => setPicked((current) => ({ ...current, [line.cartIndex]: value }))} />
                ) : (
                  <span className="shrink-0 text-[13px] text-text_secondary">× {line.requestable}</span>
                )}
              </li>
            )
          })}
        </ul>
        <label className="flex flex-col gap-1">
          <span className="text-[14px] font-semibold">{t`Reason for the return`}</span>
          <div data-testid="return-request-reason">
            <DropdownSelect
              variant="aldoo"
              placeholder={t`Choose a reason`}
              options={reasons}
              value={reasons.filter((entry) => entry.id === reason)}
              onChange={(value) => {
                const first = Array.isArray(value) ? value[0] : value
                if (first) setReason(first.id)
              }}
              searchEnabled={false}
              showCheckbox={false}
              showSelectionTick
              enableResetButton={false}
              allowEmptySelection={false}
              menuPosition="top"
            />
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[14px] font-semibold">{chosen?.note ? t`Note (required)` : t`Note (optional)`}</span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t`Tell the seller more`}
            className={`px-3 py-2 rounded-lg border bg-paper text-[15px] text-text outline-none resize-y focus:border-text ${noteMissing ? "border-error" : "border-bordered"}`}
            data-testid="return-request-note"
          />
        </label>
      </div>
    </Modal>
  )
}

const formatDateTime = (value, language) => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  try {
    return new Intl.DateTimeFormat(language || "en", { day: "numeric", month: language === "en" ? "short" : "long", hour: "2-digit", minute: "2-digit" }).format(d)
  } catch {
    return d.toLocaleString()
  }
}

/** A line between the messages: something that happened, and when. */
const Event = ({ children, at, language, tone = "text-text_secondary" }) => (
  <div className={`flex items-center gap-3 text-[11px] uppercase tracking-wide ${tone}`}>
    <span className="flex-1 h-px bg-bordered" />
    <span>
      {children}
      {at ? ` · ${formatDateTime(at, language)}` : ""}
    </span>
    <span className="flex-1 h-px bg-bordered" />
  </div>
)

/** A message: the buyer's own on the right, the seller's on the left. */
const Message = ({ mine, who, avatar, at, language, tone, children, testid }) => (
  <div className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={testid}>
    {avatar}
    <div className={`flex flex-col max-w-[85%] sm:max-w-[75%] ${mine ? "items-end" : "items-start"}`}>
      <span className="text-[12px] text-text_secondary mb-1 px-1">
        {who}
        {at ? ` · ${formatDateTime(at, language)}` : ""}
      </span>
      <div className={`rounded-2xl px-4 py-2.5 text-[14px] whitespace-pre-line break-words ${mine ? "rounded-br-sm" : "rounded-bl-sm"} ${tone}`}>{children}</div>
    </div>
  </div>
)

/**
 * One request, as a conversation: the items asked back, the buyer's message
 * (the reason, what they wrote), the seller's answer, and what happened in
 * between. It folds to its header; cancel while the seller has not answered.
 */
// `returnAddress`: the seller's, shown with an accepted request whose items have not come back
function RequestRow({ order, request, number, seller, sellerInfo = null, buyer = null, onChanged, defaultOpen, returnAddress = "" }) {
  const t = useT()
  const language = useSiteLanguage()
  const [open, setOpen] = useState(defaultOpen)
  // accepted and its goods back with the seller: the request is done
  const status = STATUS[request.status === "accepted" && request.returnedAt ? "returned" : request.status] || STATUS.open
  const answered = request.status === "accepted" || request.status === "declined"
  const units = (request.lines || []).reduce((sum, line) => sum + (line.quantity || 0), 0)

  const cancel = () =>
    showPromptDialog({
      title: t`Cancel this request?`,
      message: t`The seller will not see it any more. You can send a new request later.`,
      confirmText: t`Cancel request`,
      cancelText: t`Keep it`,
      onConfirm: async () => {
        const res = await ReturnRequest({ op: "cancel", order_id: order._id, request_id: request._id }).catch((e) => ({ error: e.message }))
        if (!res?.result) return showErrorToast(t(res?.message || res?.error || "The request could not be canceled"))
        showSuccessToast(t`Request canceled`)
        onChanged?.()
      },
    })

  return (
    <li className="rounded-2xl border border-bordered overflow-hidden" data-testid="return-request">
      {/* which request, when, where it stands — and the fold */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen((value) => !value))}
        className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 bg-neutral/40 cursor-pointer select-none hover:bg-neutral/70 ${open ? "border-b border-bordered" : ""}`}
        data-testid="return-request-toggle"
      >
        <span className="font-semibold">{t("Request #%s", number)}</span>
        <span className="text-[14px] text-text_secondary">{formatDate(request.at, language)}</span>
        <span className="flex-1" />
        {answered && !request.seen && <span className="inline-flex h-[20px] items-center rounded-full px-2 text-[11px] font-bold bg-primary text-white">{t`New response`}</span>}
        <span className={`inline-flex h-[24px] items-center rounded-full px-3 text-[12px] font-bold uppercase ${status.cls}`} data-testid="return-request-status">
          {t(status.label)}
        </span>
        <ChevronDownIcon className={`w-5 h-5 shrink-0 text-text_secondary transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </div>

      {open && (
        <>
          {/* what is to be sent back */}
          <div className="px-4 py-3 border-b border-bordered">
            <div className="text-[12px] uppercase tracking-wide text-text_secondary mb-2">{t("Items to return (%s)", units)}</div>
            <div className="flex flex-col gap-2">
              {(request.lines || []).map((line, at) => (
                <div key={at} className="flex items-center gap-3">
                  <Thumb line={line} size={44} />
                  <span className="flex-1 min-w-0 text-[14px] font-semibold break-words">{describeLine(line, line.name || line.pid)}</span>
                  <span className="shrink-0 text-[14px] font-semibold tabular-nums">× {line.quantity}</span>
                </div>
              ))}
            </div>
          </div>

          {/* the conversation */}
          <div className="px-4 py-4 flex flex-col gap-4">
            <Message mine who={t`You`} avatar={<BuyerAvatar person={buyer} />} at={request.at} language={language} tone="bg-primary/10 text-text" testid="return-request-mine">
              <div className="text-[11px] uppercase tracking-wide text-text_secondary mb-0.5">{t`Reason`}</div>
              <div className="font-semibold">{t(REASON_LABELS[request.reason] || request.reason)}</div>
              {request.note && <div className="mt-1.5">{request.note}</div>}
            </Message>

            {request.status === "canceled" && <Event at={request.canceledAt} language={language}>{t`You canceled the request`}</Event>}

            {answered && (
              <Message
                who={seller || t`Seller`}
                avatar={<SellerAvatar seller={sellerInfo} />}
                at={request.resolvedAt}
                language={language}
                tone={request.status === "accepted" ? "bg-[#4caf50]/10 text-text" : "bg-[#f44336]/10 text-text"}
                testid="return-request-message"
              >
                <div className={`font-semibold mb-0.5 ${request.status === "accepted" ? "text-[#2e7d32]" : "text-[#c62828]"}`}>
                  {request.status === "accepted" ? t`The seller accepted your request` : t`The seller declined your request`}
                </div>
                {request.message || "—"}
                {/* accepted, the items not back yet: how to send them — packed, the order number on the parcel, where */}
                {request.status === "accepted" && !request.returnedAt && (
                  <div className="mt-2 rounded-md bg-white/70 px-3 py-2" data-testid="return-instructions">
                    <div className="font-semibold">{t`How to send the items back`}</div>
                    <ol className="m-0 mt-1 pl-5 list-decimal flex flex-col gap-1">
                      <li>{t`Pack the items listed above, in their original packaging if you can.`}</li>
                      <li>
                        {t`Write the order number on the parcel, or put a note with it inside:`} <strong data-testid="return-order-number">{order.orderId}</strong>
                      </li>
                      <li>{returnAddress ? t`Send the parcel to the address below.` : t`The seller will tell you where to send the parcel.`}</li>
                    </ol>
                    {returnAddress && (
                      <div className="mt-2" data-testid="return-address">
                        <div className="font-semibold">{t`Send the items to:`}</div>
                        <div className="whitespace-pre-line">{returnAddress}</div>
                      </div>
                    )}
                  </div>
                )}
              </Message>
            )}

            {request.status === "open" && <div className="text-[13px] italic text-text_secondary">{t`Waiting for the seller…`}</div>}

            {request.returnedAt && (
              <Event at={request.returnedAt} language={language} tone="text-[#2e7d32]">
                {t`Received by the seller`}
              </Event>
            )}
          </div>

          {request.status === "open" && (
            <div className="flex justify-end px-4 py-3 border-t border-bordered">
              <Button variant="secondary_s" className="min-w-0" onClick={cancel} data-testid="return-request-cancel">
                {t`Cancel request`}
              </Button>
            </div>
          )}
        </>
      )}
    </li>
  )
}

/** Who sold the order, as the buyer knows them: the merchant of its products. */
const sellerOf = (order) => (order.items || []).find((item) => item.publisher)?.publisher || null

/** The tab: a new request, then the requests made. `onChanged` reloads the order. */
export default function ReturnRequests({ order, onChanged }) {
  const t = useT()
  const language = useSiteLanguage()
  const returns = order.returns || {}
  const [dialogOpen, setDialogOpen] = useState(false)
  const requests = returns.requests || []

  return (
    <div className="flex flex-col gap-4" data-testid="order-return-requests">
      {/* until when a return can be asked for — the seller's period, or a product's own — beside the button */}
      <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
        <span className="text-[14px] text-text_secondary text-right" data-testid="return-request-period">
          {/* one notice beside the button: nothing left to ask for, else where the period stands */}
          {!returns.canRequest && requests.length > 0
            ? t`Nothing more from this order can be returned.`
            : !returns.window?.until
              ? t`The return period starts when the order is delivered.`
              : returns.window.within
                ? t("You can request a return until %s", formatDate(returns.window.until, language))
                : <span className="text-[#c62828]">{t("You are outside the return period for this order (%s days)", returns.window.companyDays || returns.window.days)}</span>}
        </span>
        {/* only when something can still be asked back: otherwise the notice beside it says why not */}
        {returns.canRequest && (
          <Button variant="primary_s" className="min-w-0" onClick={() => setDialogOpen(true)} data-testid="return-request-new">
            {t`New request`}
          </Button>
        )}
      </div>
      {requests.length === 0 ? (
        <p className="m-0 py-4 text-center text-[14px] text-text_secondary">{t`No requests yet`}</p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-3">
          {requests.map((request, index) => (
            <RequestRow
              key={request._id}
              order={order}
              request={request}
              number={requests.length - index}
              seller={returns.seller?.name || sellerOf(order)}
              sellerInfo={returns.seller}
              buyer={returns.buyer}
              returnAddress={returns.returnAddress || ""}
              onChanged={onChanged}
              // the newest, one waiting, and one with an answer not read yet open; the settled rest fold
              defaultOpen={index === 0 || request.status === "open" || ((request.status === "accepted" || request.status === "declined") && !request.seen)}
            />
          ))}
        </ul>
      )}
      {dialogOpen && (
        <NewRequestDialog
          order={order}
          returns={returns}
          onClose={() => setDialogOpen(false)}
          onSent={() => {
            setDialogOpen(false)
            onChanged?.()
          }}
        />
      )}
    </div>
  )
}
